import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useParams, useRouter, useSearchParams } from 'next/navigation';
import { Transition } from '@headlessui/react';
import { Hits, Configure, SearchBox } from 'react-instantsearch-hooks-web';
import { Category } from 'shared/types/product/Category';
import InstantSearch from 'components/HOC/InstantSearch';
import GoogleAnalyticsMiddleware from 'components/HOC/InstantSearch/middlewares/GoogleAnalyticsMiddleware';
import { mapProduct } from 'helpers/algolia/map-product';
import { useFormat } from 'helpers/hooks/useFormat';
import useMediaQuery from 'helpers/hooks/useMediaQuery';
import useScrollBlock from 'helpers/hooks/useScrollBlock';
import { mediumDesktop } from 'helpers/utils/screensizes';
import LocalizedIndex from 'providers/algolia/localized-index';
import SearchItem from './search-item';
import Overlay from '../overlay';
import { MagnifyingGlassIcon as SearchIcon, XMarkIcon as CloseIcon, XMarkIcon } from '@heroicons/react/24/solid';
import CustomStats from './customstats';
import Button from '../button';
import ContentSearchItem from './content-search';
import debounce from 'lodash.debounce';
import { localeMapping } from 'helpers/constants/localeMapping';
import { CustomSearchBox } from './searchBox';
import { useAccount } from 'frontastic';

interface Props {
  setShowSearchBar?: any;
  searchedQuery?: string;
  showSearchBar?: boolean;
  searchPage?: boolean;
}

const Search: React.FC<Props> = ({ setShowSearchBar, showSearchBar, searchedQuery, searchPage }) => {
  const { locale, slug } = useParams();
  const form = useRef<HTMLFormElement>(null);
  const router = useRouter();
  const searchParams = useSearchParams();
  const { formatMessage } = useFormat({ name: 'common' });
  const { account } = useAccount();
  const searchQuery = searchParams.get('query') as string;

  const [focused, setFocused] = useState(false);
  const [numberOfHits, setNumberOfHits] = useState(0);
  const [productHits, setProductHits] = useState<any>();
  const [numberOfContentHits, setNumberOfContentHits] = useState(0);
  const [contentHits, setContentHits] = useState<any>();
  const [inputValue, setInputValue] = useState(searchedQuery ?? (searchQuery && slug != 'no-result-found' ? searchQuery : ''));
  const [debouncedQuery, setDebouncedQuery] = useState(inputValue);
  const [contentQuery, setContentQuery] = useState('');
  const [searchedViaEnter, setSearchedViaEnter] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const { blockScroll } = useScrollBlock();
  // const searchContainerRef = useRef<HTMLDivElement>(null);

  const onFocus = useCallback(() => {
    setFocused(true);
    blockScroll(true);
  }, []);

  const onBlur = useCallback(
    debounce(() => {
      blockScroll(false);
      //setFocused(false);
      blockScroll(false)
      if (searchPage == true) {
        // setQuery('');
        setFocused(false);
      }
    }, 500),
    [],
  );

  const cleanUp = useCallback(() => {
    setInputValue('');
    setDebouncedQuery('');
    setContentQuery('');
    setIsSearching(false);
    //setShowSearchBar(false);
    if (setShowSearchBar) {
      setShowSearchBar(false);
    }
  }, [])

  // const handleClickOutside = useCallback(
  //   (event: MouseEvent) => {
  //     if (searchPage) return; 

  //     if (searchContainerRef.current && !searchContainerRef.current.contains(event.target as Node)) {
  //       setFocused(false);
  //       setShowSearchBar(false);
  //       setQuery('');
  //       setProductQuery('');
  //     }
  //   },
  //   [searchPage, setShowSearchBar], 
  // );

  // useEffect(() => {
  //   document.addEventListener('mousedown', handleClickOutside);
  //   return () => {
  //     document.removeEventListener('mousedown', handleClickOutside);
  //   };
  // }, [handleClickOutside]);

  const [isDesktop] = useMediaQuery(mediumDesktop);

  const debouncedSetQuery = useRef(
    debounce((value: string) => {
      setDebouncedQuery(value);
    }, 400)
  ).current;

  useEffect(() => {
    debouncedSetQuery(inputValue);
  }, [inputValue, debouncedSetQuery]);

  useEffect(() => {
    if (searchedQuery) {
      setInputValue(searchedQuery);
    } else if (searchQuery && slug != 'no-result-found') {
      setInputValue(searchQuery);
    }
  }, [searchQuery, searchedQuery]);

  useEffect(() => {
    if (showSearchBar) {
      setFocused(true);
      const inputElement = form?.current?.querySelector('input');
      if (inputElement) {
        inputElement.focus();
      }
    }
  }, [showSearchBar]);
  useEffect(() => {
    return () => {
      setIsSearching(false);
    };
  }, []);

  // Reset loading state when search query changes (means we've navigated to search page)
  useEffect(() => {
    if (searchQuery && isSearching) {
      setIsSearching(false);
    }
  }, [searchQuery, isSearching]);
  const onChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
  }, []);

  const onKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      setSearchedViaEnter(true);
    }
    if (e.key === 'Delete') {
      if (setShowSearchBar) {
        setShowSearchBar(false);
      } else {
        cleanUp();
      }
    }
  }, []);

  const onSubmit = useCallback(() => {
    const currentQuery = form.current?.querySelector('input')?.value ?? inputValue;

    if (!currentQuery || currentQuery.trim() === '') {
      //setShowSearchBar(false);
      if (setShowSearchBar) {
        setShowSearchBar(false);
      }
      return;
    }
    setIsSearching(true);
    if (productHits?.length === 1) {
      const hit = mapProduct(productHits[0], locale, currentQuery);
      if (hit._url) {
        router.push(hit?._url);
      }
    } else {
      router.push(`/search?query=${encodeURIComponent(currentQuery)}`);
    }

    // cleanUp();
    setSearchedViaEnter(false);
    if (
      searchedViaEnter &&
      (!productHits || (Array.isArray(productHits) && productHits.length === 0)) &&
      (!contentHits || (Array.isArray(contentHits) && contentHits.length === 0))
    ) {
      router.push(`/no-result-found?query=${encodeURIComponent(currentQuery)}`);
    }
    setFocused(false);
    setIsSearching(true);
    setInputValue('');
    setDebouncedQuery('');
    setContentQuery('');
  }, [searchedViaEnter, inputValue, router, productHits, contentHits, locale]);

  const ontxtChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value; // full current value
    console.log("Current input value:", value); // debug
    setInputValue(value); // update state with full string
  }, []);

  const onSubmitViewAll = useCallback(() => {
    if (!inputValue || inputValue.trim() === '') return;
    setFocused(false);
    router.push(`/search?query=${encodeURIComponent(inputValue)}`);
  }, [inputValue, router]);

  return (
    <InstantSearch>
      {/* <GoogleAnalyticsMiddleware /> */}
      <Configure enablePersonalization={!debouncedQuery} />
      {isSearching && (
        <div className="fixed inset-0 z-[9999] flex flex-col items-center justify-center bg-white bg-opacity-95">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-blue-600 mb-4"></div>
          <p className="text-gray-600 text-lg font-medium">Searching...</p>
        </div>
      )}
      {focused && (
        <Overlay
          onClick={() => {
            cleanUp(); // Call cleanUp function
            if (setShowSearchBar) {
              setShowSearchBar(false); // Call setShowSearchBar with false if it exists
            }
          }}
        />
      )}

      <div className={searchQuery ? 'relative z-[10]' : 'relative z-[300] xl:px-20'}>
        {searchQuery && slug != 'no-result-found' && (
          <div className="absolute right-[1rem] top-[50%] z-[12] !h-24 !w-24 -translate-y-1/2 stroke-2 text-primary-black hover:cursor-pointer">
            <XMarkIcon
              className=""
              onClick={() => {
                //setShowSearchBar(true);
                if (setShowSearchBar) {
                  setShowSearchBar(false);
                }
                setFocused(false);
                setInputValue('');
                setDebouncedQuery('');
                setContentQuery('');
                blockScroll(false);
              }}
            />
          </div>
        )}
        {!searchQuery || slug === 'no-result-found' ? (
          <SearchBox
            defaultValue={inputValue}
            onFocus={onFocus}
            onBlur={onBlur}
            placeholder={`${formatMessage({
              id: isDesktop ? 'search.placeholder' : 'search.placeholder.mobile',
              defaultMessage: 'Type to search',
            })}`}
            classNames={{
              root: `relative z-50 border-b`,
              form: 'relative flex items-stretch bg-[#F7F7F7] w-full quick-search',

              input:
                'placeholder:text-16 pl-67 placeholder:3xl:text-24 placeholder:text-[#333] placeholder:font-medium placeholder:truncate bg-[#F7F7F7]  box-content border-none grow focus:outline-none transition pl-48 md:pr-24 md:pl-42 pt-20 pb-18  lg:pt-12 lg:pb-10  3xl:pt-20 3xl:pb-18 ',
              submit: `hidden border-l transition border-neutral-400 px-16 py-10 shrink-0 ${focused ? 'bg-primary-black' : 'bg-white'
                }`,
              submitIcon: `w-18 h-18 stroke-0 ${focused ? 'fill-white' : 'fill-secondary-black'}`,
              reset: 'hidden absolute right-[1.5rem] top-1/2 -translate-y-1/2',
              resetIcon: 'w-24 h-24 lg:w-18 lg:h-18 3xl:w-24 3xl:h-24 fill-black stroke-10',
              loadingIndicator: 'hidden',
            }}
            onKeyDown={onKeyDown}
            onInput={onChange}
            onSubmit={onSubmit}
            formRef={form}
          />
        ) : (
          <CustomSearchBox
            searchQuery={inputValue}
            setSearchQuery={setInputValue}
            onFocus={onFocus}
            onBlur={onBlur}
            onChange={ontxtChange}
            onSubmit={onSubmit}
            onKeyDown={onKeyDown}
            placeholder={`${formatMessage({
              id: isDesktop ? 'search.placeholder' : 'search.placeholder.mobile',
              defaultMessage: 'Type to search',
            })}`}
          />
        )}
        {inputValue && inputValue?.length > 1 && (
          <Transition
            show={focused && inputValue.length !== 0}
            className={`absolute bottom-0 left-0 max-h-[65vh] w-full translate-y-full overflow-auto bg-white px-40 py-20 lg:py-8 3xl:py-20 ${searchQuery ? 'xl:mx-0' : 'xl:mx-20 xl:w-[calc(100%-40px)]'
              } xl:max-h-[unset]`}
            enter="transition duration-75"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="transition duration-150"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="flex flex-col lg:flex-row">
              <div className="flex-1 pr-40">
                <LocalizedIndex type="products" isSearch={true}>
                  <Configure
                    hitsPerPage={5}
                    query={debouncedQuery}
                    // filters={account?.isInternalUser !== true ? 'NOT searchableAttributes.IsGWAInternal:true' : ''}
                    attributesToHighlight={[]}
                    attributesToSnippet={[]}
                    attributesToRetrieve={[
                      "name",
                      "slug",
                      "categoryKeys",
                      "masterVariantID",
                      "searchableAttributes.IsNew",
                      "searchableAttributes.range",
                      'searchableAttributes.builder-range',
                      // 'searchableAttributes.IsGWAInternal',
                      "attributes.range",
                      "variants.id",
                      "variants.key",
                      "variants.sku",
                      "variants.images",
                      "variants.attributes.colour",
                      "variants.attributes.item-code",
                      "variants.attributes.item-name",
                      "variants.attributes.exclusive-product-NZ",
                      "variants.attributes.range",
                    ]}
                  />
                  <Configure
                    hitsPerPage={5}
                    query={debouncedQuery}
                  // filters={account?.isInternalUser !== true ? 'NOT searchableAttributes.IsGWAInternal:true' : ''}
                  />
                  <div className="mb-12 text-20 font-medium text-[#333] lg:mb-4 lg:text-16 3xl:mb-12 3xl:text-20">
                    {formatMessage({ id: 'product.suggestions', defaultMessage: 'Product Suggestions' })}
                  </div>
                  {inputValue?.trim() === '' && (
                    <span className="text-16 font-medium text-[#333] lg:text-12 3xl:text-16">
                      {formatMessage({ id: 'no.result.found', defaultMessage: 'No result found' })}
                    </span>
                  )}
                  {numberOfHits === 0 && inputValue?.trim() !== '' && (
                    <span className="text-16 font-medium text-[#333] lg:text-12 3xl:text-16">
                      {formatMessage({ id: 'no.product.found', defaultMessage: 'No product found' })}
                    </span>
                  )}
                  {inputValue?.trim() !== '' && (
                    <div className="">
                      <Hits
                        hitComponent={({ hit }) => {
                          return (
                            <SearchItem
                              hit={{ ...hit, ...mapProduct(hit, locale, debouncedQuery) }}
                              onClick={() => {
                                if (setShowSearchBar) {
                                  setShowSearchBar(false);
                                }
                                setInputValue('');
                                setDebouncedQuery('');
                                setContentQuery('');
                                blockScroll(false);
                              }}
                            />
                          );
                        }}
                        classNames={{
                          root: '',
                          list: 'flex flex-col gap-8 lg:gap-2 3xl:gap-8',
                        }}
                      />

                      <CustomStats
                        setNumberOfHits={setNumberOfHits}
                        setProductHits={setProductHits}
                        setProductQuery={setContentQuery}
                        searchQuery={debouncedQuery}
                      />

                      {(numberOfHits > 5 || numberOfContentHits > 5) && debouncedQuery.length > 0 && (
                        <Button
                          className="mt-22 !cursor-pointer bg-white !p-0 !px-8 !pb-10 text-16 font-medium !text-[#333] underline underline-offset-2 hover:!bg-white hover:!text-[#666] lg:mt-8 lg:text-12 2xl:mt-22 3xl:text-16"
                          onClick={onSubmitViewAll}
                        >
                          {formatMessage({ id: 'view.all', defaultMessage: 'View All' })}
                        </Button>
                      )}
                    </div>
                  )}
                </LocalizedIndex>
              </div>

              <div className="box-border  flex-1 border-t border-neutral-400 pt-30 lg:border-l lg:border-t-0 lg:pl-30 lg:pt-0">
                <LocalizedIndex type="content" isSearch={true}>
                  <Configure
                    hitsPerPage={5}
                    facets={['*']}
                    filters={`tags:b2c AND tags:methven AND locale:en-${locale}`}
                    attributesToHighlight={[]}
                    attributesToSnippet={[]}
                    attributesToRetrieve={[
                      "url",
                      "title",
                      "seo.meta_image.url",
                      "seo.meta_title"
                    ]}
                    query={contentQuery && contentQuery?.length > 0 ? contentQuery : debouncedQuery}
                  />
                  <div className="mb-12 text-20  font-medium text-[#333] lg:text-16 3xl:text-20">
                    {formatMessage({ id: 'content.suggestions', defaultMessage: 'Content Search Suggestions' })}
                  </div>
                  <CustomStats setNumberOfHits={setNumberOfContentHits} setProductHits={setContentHits} />
                  {numberOfContentHits === 0 && debouncedQuery?.trim() !== '' && (
                    <span className="text-16  font-medium text-[#333] lg:text-12 3xl:text-16">
                      {formatMessage({ id: 'no.content.found', defaultMessage: 'No content found' })}
                    </span>
                  )}
                  {debouncedQuery?.trim() !== '' && (
                    <>
                      <Hits
                        hitComponent={({ hit }) => (
                          <ContentSearchItem
                            hit={hit}
                            onClick={() => {
                              if (setShowSearchBar) {
                                setShowSearchBar(false);
                              }
                              setInputValue('');
                              setDebouncedQuery('');
                              setContentQuery('');
                              blockScroll(false);
                            }}
                          />
                        )}
                        classNames={{
                          root: '',
                          list: 'flex flex-col gap-8 lg:gap-2 3xl:gap-8',
                        }}
                      />
                    </>
                  )}
                </LocalizedIndex>
              </div>
            </div>
          </Transition>
        )}
      </div>
    </InstantSearch>
  );
};

export default Search;
